import { useTranslation } from "react-i18next";

import { Box, InputAdornment, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../formik/FormikDecimalTextField";
import { IOptionItem } from "../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "../../../../../../formik/formik-date-picker/FormikDatePicker";
import { EditableRowsTable } from "../../../../../../table/EditableRowsTable";
import { BalanceEntryFormValues, ClaimFormValues } from "../../ClaimFormFields.types";
import { SectionHeader } from "../section-header/SectionHeader";
import { useBalanceHistory } from "./hooks/useBalanceHistory";

interface Props {
  formik: FormikProps<ClaimFormValues>;
  entryTypeOptions: IOptionItem[];
}

function entryPath<K extends keyof BalanceEntryFormValues>(index: number, field: K): `balanceEntries.${number}.${K}` {
  return `balanceEntries.${index}.${field}`;
}

export function BalanceHistoryTable({ formik, entryTypeOptions }: Props) {
  const { t } = useTranslation();
  const { entries, computeSummary, addEntry, removeEntry } = useBalanceHistory(formik);

  return (
    <Box>
      <SectionHeader>{t("claims.form.balanceHistorySection")}</SectionHeader>
      <EditableRowsTable
        columns={[
          { label: t("claims.balanceHistory.date"), size: 4, required: true },
          { label: t("claims.balanceHistory.amount"), size: 3, required: true },
          { label: t("claims.balanceHistory.entryType"), size: 4 },
          { size: 1 },
        ]}
        rows={entries.map((_, originalIndex) => ({
          key: String(originalIndex),
          cells: (
            <>
              <Box sx={{ flex: "0 0 33.33%" }}>
                <FormikDatePicker formik={formik} path={entryPath(originalIndex, "date")} clearable />
              </Box>
              <Box sx={{ flex: "0 0 25%" }}>
                <FormikDecimalTextField
                  formik={formik}
                  path={entryPath(originalIndex, "amount")}
                  minValue={0}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormikAutocompleteSelect
                  formik={formik}
                  path={entryPath(originalIndex, "entryType")}
                  options={entryTypeOptions}
                />
              </Box>
            </>
          ),
        }))}
        addRowLabel={t("claims.balanceHistory.addRow")}
        onAddRow={addEntry}
        onRemoveRow={removeEntry}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", textAlign: "right" }}>
        {computeSummary()}
      </Typography>
    </Box>
  );
}
